<template>
  <div 
    v-if="isReady" 
    class="ai-assistant-button" 
    @click="handleClick"
  >
    <el-button type="primary" circle>
      <el-icon><ChatDotRound /></el-icon>
    </el-button>
  </div>

  <AIAssistantDialog
    v-model="showDialog"
    :module-type="currentModule"
    :user-id="currentUserId"
    :title="'智能助手'"
  />
</template>

<script setup>
import { ChatDotRound } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { ref, onMounted, watch, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import AIAssistantDialog from './AIAssistantDialog.vue'
import { MODULE_TYPES } from '@/constants/ai-assistant'

const router = useRouter()
const userStore = useUserStore()
const showDialog = ref(false)
const currentModule = ref(MODULE_TYPES.GENERAL)

// 使用计算属性获取用户ID
const currentUserId = computed(() => {
  const userId = userStore.userId
  console.log('[AI助手按钮] 当前用户ID:', userId)
  return userId
})

// 检查是否准备好显示按钮
const isReady = computed(() => {
  const ready = userStore.isLoggedIn && !!currentUserId.value
  console.log('[AI助手按钮] 按钮就绪状态:', {
    isLoggedIn: userStore.isLoggedIn,
    userId: currentUserId.value,
    ready,
    timestamp: new Date().toISOString()
  })
  return ready
})

// 处理点击事件
const handleClick = () => {
  console.log('[AI助手按钮] 点击事件触发:', {
    currentModule: currentModule.value,
    userId: currentUserId.value,
    isReady: isReady.value,
    showDialog: showDialog.value,
    timestamp: new Date().toISOString()
  })
  
  if (!currentModule.value) {
    ElMessage.warning('未能识别当前模块类型')
    return
  }
  
  showDialog.value = true
}

// 根据路由判断当前模块
const getCurrentModule = () => {
  const route = router.currentRoute.value
  console.log('[AI助手按钮] 当前路由:', {
    path: route.path,
    meta: route.meta,
    timestamp: new Date().toISOString()
  })
  
  // 必须从路由 meta 中获取模块类型
  if (!route.meta?.moduleType) {
    console.warn('[AI助手按钮] 路由未配置模块类型:', route.path)
    return MODULE_TYPES.GENERAL // 默认返回通用模块，而不是抛出错误
  }
  
  return route.meta.moduleType
}

// 监听路由变化
watch(
  () => router.currentRoute.value.path,
  (newPath, oldPath) => {
    console.log('[AI助手按钮] 路由变化:', {
      from: oldPath,
      to: newPath,
      oldModule: currentModule.value,
      timestamp: new Date().toISOString()
    })
    // 只在按钮可见时更新模块类型
    if (isReady.value) {
      currentModule.value = getCurrentModule()
      console.log('[AI助手按钮] 更新后的模块:', {
        moduleType: currentModule.value,
        path: newPath,
        timestamp: new Date().toISOString()
      })
    }
  },
  { immediate: true }
)

// 监听用户状态变化
watch(
  () => userStore.isLoggedIn,
  async (isLoggedIn, wasLoggedIn) => {
    console.log('[AI助手按钮] 登录状态变化:', {
      from: wasLoggedIn,
      to: isLoggedIn,
      userId: userStore.userId,
      timestamp: new Date().toISOString()
    })
    if (isLoggedIn && !userStore.userInfo) {
      try {
        await userStore.getUserInfo()
        console.log('[AI助手按钮] 获取用户信息成功:', {
          userInfo: userStore.userInfo,
          timestamp: new Date().toISOString()
        })
      } catch (error) {
        console.error('[AI助手按钮] 获取用户信息失败:', {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        })
      }
    }
  }
)

onMounted(async () => {
  console.log('[AI助手按钮] 组件挂载:', {
    isLoggedIn: userStore.isLoggedIn,
    userId: userStore.userId,
    path: router.currentRoute.value.path,
    timestamp: new Date().toISOString()
  })
  if (userStore.isLoggedIn && !userStore.userInfo) {
    try {
      await userStore.getUserInfo()
      console.log('[AI助手按钮] 挂载时获取用户信息成功:', {
        userInfo: userStore.userInfo,
        timestamp: new Date().toISOString()
      })
    } catch (error) {
      console.error('[AI助手按钮] 挂载时获取用户信息失败:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      })
    }
  }
  // 只在按钮可见时设置模块类型
  if (isReady.value) {
    currentModule.value = getCurrentModule()
    console.log('[AI助手按钮] 挂载完成:', {
      moduleType: currentModule.value,
      path: router.currentRoute.value.path,
      timestamp: new Date().toISOString()
    })
  }
})
</script>

<style lang="scss" scoped>
.ai-assistant-button {
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 3000;
  transition: all 0.3s ease;
  cursor: pointer;
}

.ai-assistant-button:hover {
  transform: translateY(-5px);
}

.ai-assistant-button .el-button {
  width: 50px;
  height: 50px;
  font-size: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.ai-assistant-button .el-button:hover {
  background: #66b1ff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

@media screen and (max-width: 768px) {
  .ai-assistant-button {
    right: 20px;
    bottom: 20px;
  }
  
  .ai-assistant-button .el-button {
    width: 45px;
    height: 45px;
    font-size: 20px;
  }
}
</style> 